import React, { useMemo, useState, useEffect, useCallback } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useReportFormat from "../useReportFormat";

const formatMonthLabel = (ym) => {
  const d = new Date(ym + "-01");
  return d.toLocaleString("en-US", { month: "short" }) + " " + d.getFullYear();
};

const yFormatter = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${Math.round(val / 1_000)}K`;
  return String(Math.round(val));
};

const pickerSlotProps = {
  textField: {
    size: "small",
    sx: {
      width: 150,
      "& .MuiInputBase-root": { fontSize: "0.78rem", fontWeight: 500 },
      "& .MuiInputBase-input": { padding: "5px 8px" },
    },
  },
};

const TrendsChart = ({ months = [] }) => {
  const formatCurrency = useReportFormat();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const lastMonth = months[months.length - 1]?.month ?? null;
  const availableMonths = useMemo(() => new Set(months.map((m) => m.month)), [months]);
  const shouldDisableMonth = useCallback((date) => !availableMonths.has(date.format("YYYY-MM")), [availableMonths]);

  const sortedAvailable = useMemo(() => Array.from(availableMonths).sort(), [availableMonths]);
  const defaultEndYM = lastMonth ?? dayjs().startOf("month").format("YYYY-MM");
  const targetStartYM = dayjs(defaultEndYM + "-01").subtract(6, "month").format("YYYY-MM");
  const defaultStartYM = sortedAvailable.find((m) => m >= targetStartYM) ?? sortedAvailable[0] ?? targetStartYM;

  const resetRange = useCallback(() => {
    setEndDate(dayjs(defaultEndYM + "-01"));
    setStartDate(dayjs(defaultStartYM + "-01"));
  }, [defaultEndYM, defaultStartYM]);

  useEffect(() => {
    resetRange();
  }, [resetRange]);

  const startYM = startDate?.format("YYYY-MM") ?? "";
  const endYM = endDate?.format("YYYY-MM") ?? "";
  const isFiltered = startYM !== defaultStartYM || endYM !== defaultEndYM;

  const visibleMonths = useMemo(() => {
    return months.filter((m) => {
      if (startYM && m.month < startYM) return false;
      if (endYM && m.month > endYM) return false;
      return true;
    });
  }, [months, startYM, endYM]);

  const categories = visibleMonths.map((m) => formatMonthLabel(m.month));
  const series = [
    {
      data: visibleMonths.map((m) => Math.round(m.actual ?? 0)),
      label: "Actual Spend",
      color: "#002045",
      valueFormatter: (v) => formatCurrency(v),
    },
    {
      data: visibleMonths.map((m) => Math.round(m.committed ?? 0)),
      label: "Committed",
      color: "#d97706",
      valueFormatter: (v) => formatCurrency(v),
    },
  ];
  const hasData = visibleMonths.some(
    (m) => (m.actual ?? 0) > 0 || (m.committed ?? 0) > 0
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: 32,
          border: "1px solid #f0f4ff",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#002045", margin: 0, marginBottom: 2 }}>
              Monthly Budget vs. Actual Trends
            </p>
            <p style={{ fontSize: "0.78rem", color: "#64748b", margin: 0, fontWeight: 500 }}>
              Actual spend vs. committed (pending invoices)
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            <DatePicker
              label="From"
              views={["year", "month"]}
              openTo="month"
              value={startDate}
              maxDate={endDate ?? undefined}
              onChange={setStartDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>–</span>
            <DatePicker
              label="To"
              views={["year", "month"]}
              openTo="month"
              value={endDate}
              minDate={startDate ?? undefined}
              onChange={setEndDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            {isFiltered && (
              <button
                type="button"
                onClick={resetRange}
                style={{
                  border: "none",
                  background: "none",
                  color: "#7c3aed",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 2px",
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {!hasData ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            No spending data yet
          </div>
        ) : (
          <BarChart
            series={series}
            xAxis={[{ data: categories, scaleType: "band" }]}
            yAxis={[{ valueFormatter: yFormatter }]}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 6,
                itemGap: 16,
                labelStyle: { fontSize: 12, fontWeight: 500, fill: "#475569" },
              },
            }}
          />
        )}
      </div>
    </LocalizationProvider>
  );
};

export default TrendsChart;
